import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import CommonLoader from '../CommonLoader';
import { useTranslation } from 'react-i18next';
import { useMasterLabel } from '../../hooks/useMasterLabel';

// Per-row translated specialty label for the appointments-by-specialty legend.
const SpecialtyLegendLabel = ({ specialty }) => {
  const label = useMasterLabel('masterSpecialty', specialty?.specialtyId, 'name', specialty?.name || '');
  return <p className="text-xs sm:text-sm font-semibold text-gray-700 capitalize">{label}</p>;
};

const AppointmentAnalytics = ({ dateRange, selectedPeriod, customStartDate, customEndDate, specialtyId, addressTypeName, appointmentAnalytics, loading, embedded }) => {
  const { t } = useTranslation('dashboard');

  const appointmentLoading = loading;

  // Get appointment data from API or use default
  const getAppointmentData = () => {
    if (appointmentAnalytics?.data) {
      const specialties = appointmentAnalytics.data.map(item => ({
        specialtyId: item.specialtyId,
        name: item.specialtyName,
        count: item.appointmentCount,
        color: item.color,
      }));

      return {
        total: appointmentAnalytics.totalAppointments || 0,
        specialties: specialties,
      };
    }

    return {
      total: 0,
      specialties: [],
    };
  };

  const currentAppointmentData = getAppointmentData();

  const chartData = currentAppointmentData.specialties.map((spec) => ({
    name: spec.name,
    value: spec.count,
    color: spec.color,
  }));

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      const total = currentAppointmentData.total;
      const percentage = total ? ((data.value / total) * 100).toFixed(2) : 0;
      return (
        <div data-testid="appointment-analytics-tooltip" className="bg-white p-2 border border-gray-200 rounded shadow-lg">
          <p data-testid="appointment-analytics-tooltip-name" className="font-semibold text-gray-900">{data.name}</p>
          <p data-testid="appointment-analytics-tooltip-count" className="text-sm text-gray-700">{data.value} {t('dashboard.appointments', 'appointments')}</p>
          <p data-testid="appointment-analytics-tooltip-percent" className="text-xs text-gray-500">({percentage}%)</p>
        </div>
      );
    }
    return null;
  };

  return (
    <motion.div
      data-testid="appointment-analytics"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.4 }}
      className={embedded ? "w-full" : "bg-white w-full rounded-xl p-4 border border-gray-200/50 shadow-md"}
    >
      <div data-testid="appointment-analytics-header" className={embedded ? "hidden" : "flex flex-col items-start gap-3 mb-4"}>
        <h3 data-testid="appointment-analytics-title" className="text-base sm:text-lg font-bold text-gray-900 flex items-center">
          <Calendar data-testid="calendar-icon" className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-[#4ABDBD]" />
          {t('dashboard.appointment_analytics')}
        </h3>
      </div>
      <AnimatePresence mode="wait">
        {appointmentLoading ? (
          <div data-testid="appointment-analytics-loading" className="flex flex-col items-center justify-center py-12">
            <CommonLoader />
            <span data-testid="appointment-analytics-loading-text" className="text-sm text-gray-600 mt-2">{t('dashboard.loading_appointments')}</span>
          </div>
        ) : (
          <motion.div
            key={selectedPeriod}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="flex flex-col xl:flex-row xl:gap-4"
          >
            <div data-testid="appointment-analytics-legend-side" className="space-y-4 xl:w-1/2">
              {currentAppointmentData.specialties.length > 0 ? (
                <div data-testid="appointment-analytics-specialty-list" className="space-y-2 max-h-52 overflow-y-auto pr-1 custom-scrollbar">
                  {currentAppointmentData.specialties.map((specialty, specIndex) => (
                    <motion.div
                      key={specialty.name}
                      data-testid={`appointment-analytics-specialty-row-${specialty.specialtyId || specialty.name}`}
                      initial={{ opacity: 0, x: 10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: specIndex * 0.05 }}
                      className="flex items-center justify-between p-1.5"
                    >
                      <div className="flex items-center space-x-2">
                        <div
                          data-testid={`appointment-analytics-specialty-color-${specialty.specialtyId || specialty.name}`}
                          className="w-3 h-3 sm:w-4 sm:h-4 rounded-full shadow-sm"
                          style={{ backgroundColor: specialty.color }}
                        ></div>
                        <SpecialtyLegendLabel specialty={specialty} />
                      </div>
                      <div className="flex items-center space-x-2">
                        <p data-testid={`appointment-analytics-specialty-count-${specialty.specialtyId || specialty.name}`} className="text-xs sm:text-sm font-bold text-gray-900">
                          {specialty.count}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div data-testid="appointment-analytics-empty-state" className="flex flex-col items-center justify-center py-8">
                  <Calendar data-testid="appointment-analytics-empty-icon" className="w-12 h-12 text-gray-300 mb-2" />
                  <p data-testid="appointment-analytics-empty-text" className="text-sm text-gray-600">{t('dashboard.no_appointment_data')}</p>
                </div>
              )}
            </div>
            <div data-testid="appointment-analytics-chart-side" className="xl:w-1/2">
              <div className="">
                <div data-testid="appointment-analytics-chart-container" className="h-48 sm:h-52 md:h-52 relative">
                  {currentAppointmentData.specialties.length > 0 ? (
                    <>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={chartData}
                            cx="50%"
                            cy="50%"
                            innerRadius="40%"
                            outerRadius="70%"
                            dataKey="value"
                            label={false}
                          >
                            {chartData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip content={<CustomTooltip />} />
                        </PieChart>
                      </ResponsiveContainer>
                      <div data-testid="appointment-analytics-total-overlay" className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                        <p data-testid="appointment-analytics-total-value" className="text-sm font-bold text-gray-900">
                          {currentAppointmentData.total}
                        </p>
                        <p data-testid="appointment-analytics-total-label" className="text-xs font-medium text-gray-500">
                          {t('dashboard.total')}
                        </p>
                      </div>
                    </>
                  ) : (
                    <div data-testid="appointment-analytics-no-chart" className="h-full flex items-center justify-center">
                      <p data-testid="appointment-analytics-no-chart-text" className="text-sm text-gray-400">{t('dashboard.no_chart_data')}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default AppointmentAnalytics;
